import React, { useState } from 'react'
import { toast } from 'sonner'
import { Eye, Check, X, Clock, User, Calendar, Loader2, AlertCircle } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import { Separator } from '@/components/ui/separator'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog'
import { EmptyState } from '@/components/shared/EmptyState'
import { leaveService } from '@/services/leaveService'

const LeaveManagementTable = ({ leaves, onUpdate }) => {
  const [loading, setLoading] = useState(null)
  const [selectedLeave, setSelectedLeave] = useState(null)
  const [isModalVisible, setIsModalVisible] = useState(false)

  const handleApprove = async (id) => {
    setLoading(id)
    try {
      const response = await leaveService.updateLeaveStatus(id, { status: 'approved' })
      if (response.success) {
        toast.success('Leave request approved!')
        onUpdate?.()
      }
    } catch (error) {
      toast.error('Failed to approve leave request')
    } finally {
      setLoading(null)
    }
  }

  const handleReject = async (id) => {
    setLoading(id)
    try {
      const response = await leaveService.updateLeaveStatus(id, { status: 'rejected' })
      if (response.success) {
        toast.success('Leave request rejected!')
        onUpdate?.()
      }
    } catch (error) {
      toast.error('Failed to reject leave request')
    } finally {
      setLoading(null)
    }
  }

  const handleViewDetails = (leave) => {
    setSelectedLeave(leave)
    setIsModalVisible(true)
  }

  const getStatusBadge = (status) => {
    const config = {
      pending: { variant: 'outline', className: 'border-yellow-500 text-yellow-600 bg-yellow-50', icon: Clock },
      approved: { variant: 'outline', className: 'border-emerald-500 text-emerald-600 bg-emerald-50', icon: Check },
      rejected: { variant: 'outline', className: 'border-red-500 text-red-600 bg-red-50', icon: X },
      cancelled: { variant: 'outline', className: 'border-gray-400 text-gray-600 bg-gray-50', icon: AlertCircle },
    }
    const { variant, className, icon: Icon } = config[status] || config.pending
    return (
      <Badge variant={variant} className={className}>
        <Icon className="mr-1 h-3 w-3" />
        {status?.charAt(0).toUpperCase() + status?.slice(1)}
      </Badge>
    )
  }

  const getLeaveTypeInfo = (type) => {
    const typeLabels = {
      sick: { label: 'Sick Leave', color: 'bg-red-100 text-red-700' },
      vacation: { label: 'Vacation', color: 'bg-blue-100 text-blue-700' },
      personal: { label: 'Personal', color: 'bg-purple-100 text-purple-700' },
      maternity: { label: 'Maternity', color: 'bg-pink-100 text-pink-700' },
      paternity: { label: 'Paternity', color: 'bg-indigo-100 text-indigo-700' },
      emergency: { label: 'Emergency', color: 'bg-orange-100 text-orange-700' },
      other: { label: 'Other', color: 'bg-gray-100 text-gray-700' }
    }
    return typeLabels[type] || { label: type, color: 'bg-gray-100 text-gray-700' }
  }

  const calculateDays = (startDate, endDate) => {
    const start = new Date(startDate)
    const end = new Date(endDate)
    const diffTime = end - start
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1
  }

  const getInitials = (firstName, lastName) => {
    return `${firstName?.charAt(0) || ''}${lastName?.charAt(0) || ''}`.toUpperCase()
  }

  if (leaves.length === 0) {
    return (
      <Card>
        <CardContent className="py-12">
          <EmptyState
            icon="calendar"
            title="No leave requests"
            description="There are no leave requests to manage."
          />
        </CardContent>
      </Card>
    )
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg">Leave Requests</CardTitle>
          <CardDescription>Review and manage employee leave requests</CardDescription>
        </CardHeader>
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <TableHead className="font-semibold">Employee</TableHead>
              <TableHead className="font-semibold">Leave Type</TableHead>
              <TableHead className="font-semibold">Duration</TableHead>
              <TableHead className="font-semibold">Status</TableHead>
              <TableHead className="font-semibold">Requested</TableHead>
              <TableHead className="text-right font-semibold">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {leaves.map((leave) => {
              const typeInfo = getLeaveTypeInfo(leave.leave_type)
              const days = calculateDays(leave.start_date, leave.end_date)
              const isLoading = loading === leave.id
              return (
                <TableRow key={leave.id} className="group">
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <Avatar className="h-9 w-9">
                        <AvatarFallback className="bg-primary/10 text-primary text-sm font-medium">
                          {getInitials(leave.employee?.first_name, leave.employee?.last_name)}
                        </AvatarFallback>
                      </Avatar>
                      <div>
                        <p className="font-medium text-sm">{leave.employee?.first_name} {leave.employee?.last_name}</p>
                        <p className="text-xs text-muted-foreground">
                          {leave.employee?.department}
                        </p>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge variant="secondary" className={typeInfo.color}>
                      {typeInfo.label}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className="space-y-1">
                      <div className="flex items-center gap-1.5 font-medium text-sm">
                        <Calendar className="h-3.5 w-3.5 text-muted-foreground" />
                        {days} {days === 1 ? 'day' : 'days'}
                      </div>
                      <p className="text-xs text-muted-foreground">
                        {new Date(leave.start_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - {new Date(leave.end_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                      </p>
                    </div>
                  </TableCell>
                  <TableCell>{getStatusBadge(leave.status)}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {new Date(leave.requested_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleViewDetails(leave)}
                        className="h-8 px-2"
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                      {leave.status === 'pending' && (
                        <>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 px-2 text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50"
                            onClick={() => handleApprove(leave.id)}
                            disabled={isLoading}
                          >
                            {isLoading ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <Check className="h-4 w-4" />
                            )}
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 px-2 text-red-600 hover:text-red-700 hover:bg-red-50"
                            onClick={() => handleReject(leave.id)}
                            disabled={isLoading}
                          >
                            {isLoading ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <X className="h-4 w-4" />
                            )}
                          </Button>
                        </>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      </Card>

      {/* Leave Details Modal */}
      <Dialog open={isModalVisible} onOpenChange={setIsModalVisible}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Leave Request Details</DialogTitle>
            <DialogDescription>Review the complete details of this leave request</DialogDescription>
          </DialogHeader>
          {selectedLeave && (
            <div className="space-y-6">
              {/* Employee Info */}
              <div className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg">
                <Avatar className="h-10 w-10">
                  <AvatarFallback className="bg-primary/10 text-primary">
                    {getInitials(selectedLeave.employee?.first_name, selectedLeave.employee?.last_name)}
                  </AvatarFallback>
                </Avatar>
                <div>
                  <p className="font-medium">{selectedLeave.employee?.first_name} {selectedLeave.employee?.last_name}</p>
                  <p className="text-sm text-muted-foreground">{selectedLeave.employee?.department} • {selectedLeave.employee?.employee_id}</p>
                </div>
              </div>

              <Separator />

              {/* Leave Details */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Leave Type</p>
                  <Badge variant="secondary" className={getLeaveTypeInfo(selectedLeave.leave_type).color}>
                    {getLeaveTypeInfo(selectedLeave.leave_type).label}
                  </Badge>
                </div>
                <div className="space-y-1">
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Status</p>
                  {getStatusBadge(selectedLeave.status)}
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Start Date</p>
                  <p className="font-medium">{new Date(selectedLeave.start_date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })}</p>
                </div>
                <div className="space-y-1">
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">End Date</p>
                  <p className="font-medium">{new Date(selectedLeave.end_date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })}</p>
                </div>
              </div>

              <div className="p-3 bg-primary/5 rounded-lg text-center">
                <p className="text-xs text-muted-foreground">Total Duration</p>
                <p className="text-xl font-semibold text-primary">
                  {calculateDays(selectedLeave.start_date, selectedLeave.end_date)} {calculateDays(selectedLeave.start_date, selectedLeave.end_date) === 1 ? 'day' : 'days'}
                </p>
              </div>

              {selectedLeave.reason && (
                <div className="space-y-1">
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Reason</p>
                  <p className="text-sm p-3 bg-muted/50 rounded-lg">{selectedLeave.reason}</p>
                </div>
              )}

              {selectedLeave.status === 'pending' && (
                <>
                  <Separator />
                  <div className="flex gap-3">
                    <Button
                      onClick={() => {
                        handleApprove(selectedLeave.id)
                        setIsModalVisible(false)
                      }}
                      className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                    >
                      <Check className="h-4 w-4 mr-2" />
                      Approve
                    </Button>
                    <Button
                      variant="destructive"
                      onClick={() => {
                        handleReject(selectedLeave.id)
                        setIsModalVisible(false)
                      }}
                      className="flex-1"
                    >
                      <X className="h-4 w-4 mr-2" />
                      Reject
                    </Button>
                  </div>
                </>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  )
}

export default LeaveManagementTable
